import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import Header from './components/Header.js';
import Footer from './components/Footer.js';
import LoadingSpinner from './components/LoadingSpinner.js';
import ErrorBoundary from './components/ErrorBoundary.js';
import './assets/css/general.css';
import './App.css';

const Home = lazy(() => import('./pages/Home.js'));
const About = lazy(() => import('./pages/About.js'));
const DistributorPage = lazy(() => import('./pages/Distributor.js'));
const ContactPage = lazy(() => import('./pages/ContactPage.js'));
const ProductsPage = lazy(() => import('./products/Products.js'));
const ProductDetailPage = lazy(() => import('./products/ProductDetailPage.js'));
const RequestQuotePage = lazy(() => import('./products/RequestQuotePage.js'));
const BecomeSupplierPage = lazy(() => import('./pages/BecomeSupplierPage.js'));
const NotFound = lazy(() => import('./pages/NotFound.js'));

const LanguageHandler = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    const supportedLanguages = ['en', 'de'];
    if (lang && supportedLanguages.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return <Outlet />;
};

const Layout = () => {
  return (
    <>
      <Header />
      <Suspense fallback={<LoadingSpinner />}>
        <Outlet />
      </Suspense>
      <Footer />
    </>
  );
};

function App() {
  return (
    <Router>
      <HelmetProvider>
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="distributor" element={<DistributorPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:id" element={<ProductDetailPage />} />
              <Route path="request-quote" element={<RequestQuotePage />} />
              <Route path="become-supplier" element={<BecomeSupplierPage />} />
            </Route>

            <Route path="/:lang" element={<LanguageHandler />}>
              <Route path="" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="distributor" element={<DistributorPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/:id" element={<ProductDetailPage />} />
                <Route path="request-quote" element={<RequestQuotePage />} />
                <Route path="become-supplier" element={<BecomeSupplierPage />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </HelmetProvider>
    </Router>
  );
}

export default App;